import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowRight, FiCheckSquare, FiMessageCircle } from 'react-icons/fi';
import { formatMoney } from '../utils/money';
import PlanUpgradeCard from './PlanUpgradeCard';
import { getAssistantContext } from '../utils/assistantContexts';

const WEEK_GUIDANCE_CHIPS = getAssistantContext('inicio-semana').suggestions;

function GuidanceSkeleton({ box, modoOscuro }) {
  const pulse = modoOscuro ? 'bg-gray-800 animate-pulse' : 'bg-gray-200 animate-pulse';
  return (
    <div className={`rounded-2xl border p-4 space-y-3 ${box}`}>
      <div className={`h-4 w-48 rounded ${pulse}`} />
      <div className={`h-16 w-full rounded-xl ${pulse}`} />
      <div className={`h-16 w-full rounded-xl ${pulse}`} />
    </div>
  );
}

export default function WeeklyGuidanceCard({
  secureFetch,
  hasProfesional,
  modoOscuro,
  box,
  textPri,
  textSec,
  textMuted,
}) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [question, setQuestion] = useState('');
  const [asking, setAsking] = useState(false);
  const [messages, setMessages] = useState([]);
  const [clearing, setClearing] = useState(false);

  const tile = modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50';
  const accent = modoOscuro ? 'bg-[#1F80FF]/15 text-[#7eb8ff]' : 'bg-[#1F80FF]/10 text-[#004AAD]';

  const fetchGuidance = useCallback(async () => {
    if (!hasProfesional) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch('/api/asistente/semana');
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'No pudimos armar tu semana');
      setData(json);
      setMessages(Array.isArray(json.chatHistory) ? json.chatHistory : []);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [hasProfesional, secureFetch]);

  useEffect(() => {
    fetchGuidance();
  }, [fetchGuidance]);

  if (!hasProfesional) {
    return (
      <PlanUpgradeCard
        compact
        description="Mirá qué hacer esta semana: impagas prioritarias, monto en riesgo y gestiones sugeridas con plan Profesional+."
        planLabel="Profesional"
        modoOscuro={modoOscuro}
      />
    );
  }

  if (loading) return <GuidanceSkeleton box={box} modoOscuro={modoOscuro} />;

  if (error) {
    return (
      <div className={`rounded-2xl border px-4 py-3 text-sm ${box} ${modoOscuro ? 'text-red-300' : 'text-red-700'}`}>
        {error}
      </div>
    );
  }

  if (!data?.weekIn3Steps) return null;

  const steps = data.weekIn3Steps.steps || [];
  const pending = (data.monthClose?.pending || []).slice(0, 2);
  const dsoText = data.narration?.dso?.text;
  const impactText = data.narration?.impactoCobranza?.text;

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${accent}`}>
            <FiMessageCircle className="w-3 h-3" />
            Qué hacer esta semana
          </span>
          {data.period?.label && (
            <span className={`text-[10px] ${textMuted}`}>{data.period.label}</span>
          )}
        </div>
        <h2 className={`text-sm font-semibold ${textPri}`}>
          {data.weekIn3Steps.title || 'Tu semana en 3 pasos'}
        </h2>
        <p className={`text-xs mt-0.5 ${textSec}`}>
          Prioridad de cobro, riesgo y gestiones. Cifras calculadas en el servidor.
        </p>
      </div>

      <div className="p-4 space-y-2.5">
        <ol className="space-y-2">
          {steps.map((step, index) => (
            <li key={step.id}>
              <button
                type="button"
                onClick={() => navigate(step.href || '/cobranzas')}
                className={`w-full text-left rounded-xl px-3 py-2.5 transition-colors ${tile} ${
                  modoOscuro ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <span className={`mt-0.5 w-5 h-5 rounded-full text-[11px] font-semibold flex items-center justify-center shrink-0 ${accent}`}>
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-xs font-semibold ${textPri}`}>{step.title}</p>
                      {step.metric?.amountArs > 0 && (
                        <span className={`text-xs font-semibold tabular-nums shrink-0 ${textPri}`}>
                          {formatMoney(step.metric.amountArs, 'ARS')}
                        </span>
                      )}
                    </div>
                    <p className={`text-[11px] mt-0.5 leading-snug ${textSec}`}>{step.body}</p>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ol>

        {(dsoText || impactText) && (
          <p className={`text-[11px] leading-relaxed ${textSec}`}>
            {dsoText}
            {dsoText && impactText ? ' ' : ''}
            {impactText || ''}
          </p>
        )}

        {pending.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {pending.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => item.href && navigate(item.href)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-medium ${
                  modoOscuro ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                }`}
              >
                <FiCheckSquare className="w-3 h-3 opacity-70" />
                {item.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
          <p className={`text-[10px] ${textMuted}`}>{data.disclaimer}</p>
          <button
            type="button"
            onClick={() => navigate('/cobranzas')}
            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1F80FF] hover:underline"
          >
            Ir a Cobrar
            <FiArrowRight className="w-3 h-3" />
          </button>
        </div>

        <form
          className={`pt-2 border-t space-y-2 ${modoOscuro ? 'border-gray-800' : 'border-gray-100'}`}
          onSubmit={async (event) => {
            event.preventDefault();
            const text = question.trim();
            if (!text || asking) return;
            setAsking(true);
            try {
              const res = await secureFetch('/api/asistente/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: text }),
              });
              const json = await res.json();
              if (!res.ok) throw new Error(json.message || 'No pudimos responder');
              setMessages(Array.isArray(json.history) ? json.history : (prev) => [
                ...prev,
                { role: 'user', content: text },
                { role: 'assistant', content: json.reply },
              ]);
              setQuestion('');
            } catch (err) {
              setMessages((prev) => [
                ...prev,
                { role: 'user', content: text },
                { role: 'assistant', content: err.message },
              ]);
            } finally {
              setAsking(false);
            }
          }}
        >
          <p className={`text-[10px] font-semibold uppercase tracking-wide ${textMuted}`}>Preguntá por tu semana</p>
          <div className="flex flex-wrap gap-1">
            {WEEK_GUIDANCE_CHIPS.map((chip) => (
              <button
                key={chip}
                type="button"
                disabled={asking}
                onClick={() => setQuestion(chip)}
                className={`px-2 py-1 rounded-lg text-[10px] ${
                  modoOscuro ? 'bg-gray-800 text-gray-200 hover:bg-gray-700' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {chip}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              maxLength={500}
              placeholder="Ej. ¿A quién cobro primero?"
              className={`flex-1 min-w-0 rounded-xl border px-3 py-2 text-xs ${
                modoOscuro
                  ? 'bg-gray-900 border-gray-700 text-white placeholder:text-gray-500'
                  : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
              }`}
            />
            <button
              type="submit"
              disabled={asking || !question.trim()}
              className="shrink-0 px-3 py-2 rounded-xl text-xs font-semibold bg-[#1F80FF] text-white disabled:opacity-50"
            >
              {asking ? '…' : 'Preguntar'}
            </button>
          </div>
          {messages.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <p className={`text-[10px] ${textMuted}`}>Últimas consultas de esta semana</p>
                <button
                  type="button"
                  disabled={clearing || asking}
                  onClick={async () => {
                    setClearing(true);
                    try {
                      const res = await secureFetch('/api/asistente/chat', { method: 'DELETE' });
                      const json = await res.json();
                      if (!res.ok) throw new Error(json.message || 'No pudimos borrar el historial');
                      setMessages(Array.isArray(json.history) ? json.history : []);
                    } catch {
                      setMessages([]);
                    } finally {
                      setClearing(false);
                    }
                  }}
                  className={`text-[10px] font-semibold hover:underline disabled:opacity-50 ${
                    modoOscuro ? 'text-gray-400' : 'text-gray-500'
                  }`}
                >
                  {clearing ? 'Borrando…' : 'Limpiar'}
                </button>
              </div>
              <div className={`rounded-xl px-3 py-2 space-y-2 max-h-36 overflow-y-auto ${tile}`}>
                {messages.slice(-6).map((item, index) => (
                  <div key={`${item.role}-${item.at || index}`}>
                    <p className={`text-[10px] ${textMuted}`}>{item.role === 'user' ? 'Vos' : 'Asistente'}</p>
                    <p className={`text-[11px] mt-0.5 leading-relaxed ${textSec}`}>{item.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
